import { isAxiosError } from 'axios';
import { formatGenset } from '../../utils/reeferGenset';
// frontend/src/pages/yard/YardReports.tsx
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useAdditionalFieldSelection, useAdditionalFieldColumns } from '../../hooks/useAdditionalFields';
import { TanTable } from '../../components/TanTable';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import RepairOrderDrawer from '../fleetmanagement/components/RepairOrderDrawer';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import StatsGrid, { StatItem } from '../../components/StatsGrid';
import AttachPronumberModal from '../../components/AttachPronumberModal';
import api from '../../services/api';
import yardService from '../../services/yard.service';
import {
  YardInventoryItem,
  YardLocation,
  MissingContainerItem,
  YardBucketCounts,
  YardReportBucketKey,
} from '../../types/yard';
import { formatFullDateTime } from '../../utils/dateUtils';
import { usePermissions } from '../../contexts/PermissionContext';
import { useToast } from '../../contexts/ToastContext';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';

// Stat row order: yard-inventory leftmost (default selection), then the four
// dispatch-derived buckets matching the legacy Dispatch Dashboard layout.
const YARD_BUCKETS: { key: YardReportBucketKey; label: string }[] = [
  { key: 'yard-inventory', label: 'Yard Inventory' },
  { key: 'terminated-on-yard', label: 'Terminated On Yard' },
  { key: 'ssl-bare-chassis', label: 'SSL Bare Chassis On Yard' },
  { key: 'leg-yard-mismatch', label: 'Leg Yard Mismatch' },
  { key: 'missing-containers', label: 'Missing Containers' },
  { key: 'not-in-cms', label: 'Not In CMS' },
  { key: 'reefers-on-yard', label: 'Reefers On Yard' },
];

const ZERO_COUNTS: YardBucketCounts = {
  yard_inventory: 0,
  terminated_on_yard: 0,
  yard_chassis_without_equipment: 0,
  leg_yard_mismatch: 0,
  missing_containers: 0,
  not_in_cms: 0,
  reefers_on_yard: 0,
};

// Columns that don't make sense for the Not In CMS bucket — these rows have no
// pronumber by definition, so every order-derived value is null.
const NOT_IN_CMS_HIDDEN_COLS: ReadonlySet<string> = new Set([
  'order_location',
  'movetype',
  'dispatcher_name',
  'perdiem_start_date',
  'perdiem_amount',
  'drop_release_datetime',
  'drop_release_memo',
  'first_rcv_date',
  'delivery_date',
  'delivery_appt_date',
  'cutoff_datetime',
  'dispatched_to_terminate',
]);

function bucketCountFor(counts: YardBucketCounts, key: YardReportBucketKey): number {
  switch (key) {
    case 'yard-inventory': return counts.yard_inventory;
    case 'terminated-on-yard': return counts.terminated_on_yard;
    case 'ssl-bare-chassis': return counts.yard_chassis_without_equipment;
    case 'leg-yard-mismatch': return counts.leg_yard_mismatch;
    case 'missing-containers': return counts.missing_containers;
    case 'not-in-cms': return counts.not_in_cms;
    case 'reefers-on-yard': return counts.reefers_on_yard;
  }
}

type ContextType = {
  selectedYard: number | null;
  setSelectedYard: (yard: number | null) => void;
};

export default function YardReports() {
  // Try to get context from GuardLayout, fallback if not available
  let selectedYard: number | null = null;

  try {
    const context = useOutletContext<ContextType>();
    selectedYard = context?.selectedYard ?? null;
  } catch {
    // Not in GuardLayout, will use local state
  }

  const { permissions, hasAccess } = usePermissions();
  // Repair-order recnos are only clickable for users who can open the Repair
  // Orders page (the drawer fetches /repair-orders/{recno}, which requires that
  // access). Everyone else sees the recnos as plain text.
  const canViewRepairOrders = hasAccess('/fleet-management/repair-orders');
  const [searchParams] = useSearchParams();
  const yardParamAppliedRef = useRef(false);

  const [yards, setYards] = useState<YardLocation[]>([]);
  // Employee multi-yard filter. Empty = All Yards. Ignored when selectedYard
  // is provided by GuardLayout (guards see only their yard).
  const [selectedYardIds, setSelectedYardIds] = useState<number[]>([]);
  const defaultYardAppliedRef = useRef(false);

  // Auto-select the user's default yard once permissions load (employees only).
  // Pre-selects a single yard in the multi-select; user can clear or add more.
  useEffect(() => {
    if (selectedYard !== null) return;
    if (defaultYardAppliedRef.current) return;
    if (selectedYardIds.length > 0) return;
    if (!permissions?.default_yard) return;
    setSelectedYardIds([permissions.default_yard]);
    defaultYardAppliedRef.current = true;
  }, [permissions?.default_yard, selectedYard, selectedYardIds.length]);

  // Deep-link: /yard/reports?yardrecno=123 pre-selects that yard. Used by the
  // Yard Inventory History page's click-through. Runs once; marks the default-
  // yard effect as applied so it won't override the deep-linked selection.
  useEffect(() => {
    if (yardParamAppliedRef.current) return;
    if (selectedYard !== null) return; // guard mode: employee filter is ignored
    const raw = searchParams.get('yardrecno');
    if (!raw) return;
    const id = parseInt(raw, 10);
    if (!Number.isNaN(id)) {
      setSelectedYardIds([id]);
      defaultYardAppliedRef.current = true;
      yardParamAppliedRef.current = true;
    }
  }, [searchParams, selectedYard]);

  // The active bucket drives which dataset the table shows. Defaults to
  // 'yard-inventory' (the legacy On Yard report) so the page opens unchanged.
  const [activeBucket, setActiveBucket] = useState<YardReportBucketKey>('yard-inventory');
  // Every bucket owns its OWN grid layout + added-column selection (Zendesk
  // #6292: adding Customer to Leg Yard Mismatch used to land on SSL Bare
  // Chassis too, because the yardinv buckets shared one settings key). Yard
  // Inventory keeps the legacy 'yard-reports-table' key so saved layouts
  // survive; the others are namespaced by bucket.
  const inventorySettingsKey = activeBucket === 'yard-inventory' ? 'yard-reports-table' : `yard-reports-${activeBucket}`;
  // Not In CMS rows carry no order, so it offers no added columns (null key =
  // no selection to load, reads as ready immediately).
  const inventoryFields = useAdditionalFieldSelection(
    activeBucket === 'not-in-cms' ? null : inventorySettingsKey,
    { binding: 'all' },
  );
  const missingFields = useAdditionalFieldSelection('yard-reports-missing-containers', { binding: 'all' });
  const activeFields = activeBucket === 'missing-containers' ? missingFields : inventoryFields;
  const fieldsReady = activeFields.ready;
  const additionalFieldsKey = activeBucket === 'not-in-cms' ? '' : activeFields.additionalFields.join(',');

  // Data state for the currently-active bucket. The first four buckets share
  // the YardInventoryItem shape (/inventory/report); missing-containers has
  // its own row shape so it lives in a separate slot.
  const [inventory, setInventory] = useState<YardInventoryItem[]>([]);
  const [missingContainers, setMissingContainers] = useState<MissingContainerItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Counts for the stat row above the table — sourced from the new
  // /yard/inventory/bucket-counts endpoint (no global filters; just yardrecno).
  const [bucketCounts, setBucketCounts] = useState<YardBucketCounts>(ZERO_COUNTS);
  const [bucketCountsLoading, setBucketCountsLoading] = useState(false);

  // Attach Pro modal — shown when the user clicks the ATTACH PRO button on a
  // yardinv row that has no pronumber (mirrors the SSL Bare Chassis flow on
  // the Dispatch Dashboard). Captures the yardinv recno + chassisno so the
  // backend update can identify the row and the modal can label itself.
  const [attachPronumberModal, setAttachPronumberModal] = useState<
    { yardinv_recno: number; chassisno: string } | null
  >(null);

  // Order Details Drawer state — opened when a PRO is clicked.
  const { showToast } = useToast();
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // Repair Order Drawer state — opened from an Equip Repair Orders link.
  const [repairOrderRecno, setRepairOrderRecno] = useState<number | null>(null);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Load yard locations
  useEffect(() => {
    yardService.getYardLocations(true).then(setYards).catch(console.error);
  }, []);

  // Load the data for whichever bucket is currently active. Yardinv-based
  // buckets share the /inventory/report row shape and stay in `inventory`;
  // missing-containers populates its own state slot. Pass { silent: true }
  // for the 60s background refresh so we don't flicker the Loading indicator.
  // Like before, employee mode fetches the full unscoped set and applies the
  // multi-yard filter client-side; guard mode fetches just the guard's yard.
  // Each load aborts the previous in-flight request — when the user clicks
  // through buckets quickly, only the latest response wins so we don't flash
  // stale data.
  const bucketDataAbortRef = useRef<AbortController | null>(null);
  const loadBucketData = useCallback(async (opts?: { silent?: boolean }) => {
    if (!fieldsReady) return;
    bucketDataAbortRef.current?.abort();
    const controller = new AbortController();
    bucketDataAbortRef.current = controller;

    if (!opts?.silent) setLoading(true);
    try {
      switch (activeBucket) {
        case 'yard-inventory': {
          const data = await yardService.getInventoryReport(selectedYard, controller.signal, additionalFieldsKey);
          setInventory(data);
          break;
        }
        case 'terminated-on-yard':
        case 'ssl-bare-chassis':
        case 'leg-yard-mismatch':
        case 'not-in-cms':
        case 'reefers-on-yard': {
          const data = await yardService.getInventoryBucket(activeBucket, selectedYard, controller.signal, additionalFieldsKey);
          setInventory(data);
          break;
        }
        case 'missing-containers': {
          const data = await yardService.getMissingContainers(selectedYard, controller.signal, additionalFieldsKey);
          setMissingContainers(data);
          break;
        }
      }
    } catch (error: unknown) {
      // Cancelled fetches aren't errors — we just want the latest one to win.
      if ((error instanceof Error && error.name === 'CanceledError') || (isAxiosError(error) && error.code === 'ERR_CANCELED')) return;
      console.error(`Error loading bucket "${activeBucket}":`, error);
    } finally {
      // Only the latest request flips loading off. A cancelled predecessor
      // would otherwise clear the spinner mid-fetch on the new request.
      if (bucketDataAbortRef.current === controller) {
        bucketDataAbortRef.current = null;
        if (!opts?.silent) setLoading(false);
      }
    }
  }, [activeBucket, selectedYard, fieldsReady, additionalFieldsKey]);

  useEffect(() => {
    loadBucketData();
  }, [loadBucketData]);

  // Yardrecnos to scope the bucket counts to. Guards always see exactly their
  // yard (selectedYard from the GuardLayout context); employees pass through
  // whatever's in the multi-yard selection (empty = all yards).
  const countYardrecnos = useMemo<number[]>(() => {
    if (selectedYard !== null) return [selectedYard];
    return selectedYardIds;
  }, [selectedYard, selectedYardIds]);
  // Stable identity to avoid re-running the count effect on every render.
  const countYardrecnosKey = useMemo(
    () => countYardrecnos.slice().sort((a, b) => a - b).join(','),
    [countYardrecnos],
  );

  const bucketCountsAbortRef = useRef<AbortController | null>(null);
  const loadBucketCounts = useCallback(async (opts?: { silent?: boolean }) => {
    bucketCountsAbortRef.current?.abort();
    const controller = new AbortController();
    bucketCountsAbortRef.current = controller;

    if (!opts?.silent) setBucketCountsLoading(true);
    try {
      const counts = await yardService.getYardBucketCounts(countYardrecnos, controller.signal);
      setBucketCounts(counts);
    } catch (error: unknown) {
      if ((error instanceof Error && error.name === 'CanceledError') || (isAxiosError(error) && error.code === 'ERR_CANCELED')) return;
      console.error('Error loading yard bucket counts:', error);
    } finally {
      // Only the latest request flips loading off. A cancelled predecessor
      // would otherwise clear the spinner mid-fetch on the new request.
      if (bucketCountsAbortRef.current === controller) {
        bucketCountsAbortRef.current = null;
        if (!opts?.silent) setBucketCountsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countYardrecnosKey]);

  useEffect(() => {
    loadBucketCounts();
  }, [loadBucketCounts]);

  // Silent background refresh every 60 seconds. Skipped when the page tab
  // is hidden so we don't burn requests on inactive tabs.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadBucketData({ silent: true });
        loadBucketCounts({ silent: true });
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [loadBucketData, loadBucketCounts]);

  // Attach a pronumber to the captured yardinv recno, then refresh the table
  // and counts so the row's PRO populates without waiting for the 60s tick.
  const handleAttachPronumber = useCallback(async (pronumber: string) => {
    if (!attachPronumberModal) return;
    try {
      const response = await api.post('/yard/yardinv/attach-pronumber', {
        yardinv_recno: attachPronumberModal.yardinv_recno,
        pronumber,
      });
      if (response.data?.success) {
        showToast({
          type: 'success',
          message: response.data.message || 'Successfully attached pronumber',
        });
        loadBucketData({ silent: true });
        loadBucketCounts({ silent: true });
      }
    } catch (error: unknown) {
      console.error('Error attaching pronumber:', error);
      showToast({
        type: 'error',
        message: (isAxiosError<{ detail?: string }>(error) && error.response?.data?.detail) || 'Failed to attach pronumber',
      });
      // Re-throw so the modal keeps the user's input and surfaces an error.
      throw error;
    }
  }, [attachPronumberModal, showToast, loadBucketData, loadBucketCounts]);

  const yardBucketStats = useMemo<StatItem[]>(
    () =>
      YARD_BUCKETS.map((b) => ({
        id: b.key,
        label: b.label,
        value: bucketCountFor(bucketCounts, b.key),
        onClick: () => setActiveBucket(b.key),
        selected: activeBucket === b.key,
      })),
    [bucketCounts, activeBucket],
  );

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatShortDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  };

  const getPerDiemUrgency = (dateStr: string | null) => {
    if (!dateStr) return '';
    const parsed = new Date(dateStr + 'T00:00:00');
    if (isNaN(parsed.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsed.setHours(0, 0, 0, 0);
    const diffDays = Math.round((parsed.getTime() - today.getTime()) / 86_400_000);
    if (diffDays <= 0) return 'bg-red-100 dark:bg-red-900/40';
    if (diffDays === 1) return 'bg-orange-100 dark:bg-orange-900/40';
    if (diffDays === 2) return 'bg-yellow-100 dark:bg-yellow-900/40';
    return '';
  };

  const columns = useMemo<ColumnDef<YardInventoryItem>[]>(() => [
    {
      accessorKey: 'yard_name',
      header: 'Yard',
      size: 130,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 whitespace-nowrap">
          {row.original.yard_name || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'order_location',
      header: 'Order Location',
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.order_location;
        if (!v) return <span className="text-gray-400">-</span>;
        return <span className="whitespace-nowrap">{v}</span>;
      },
    },
    {
      accessorKey: 'equipno',
      header: 'Equipment #',
      size: 130,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => <span className="font-semibold">{row.original.equipno || '-'}</span>,
    },
    {
      accessorKey: 'chassisno',
      header: 'Chassis #',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const chassis = row.original.chassisno;
        if (!chassis) return '-';
        if (row.original.gwi_chassis_exists) {
          return (
            <a
              href={`/data-management/equipment-list?search=${encodeURIComponent(chassis)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {chassis}
            </a>
          );
        }
        return chassis;
      },
    },
    {
      accessorKey: 'trucknumber',
      header: 'Truck #',
      size: 100,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.trucknumber;
        if (!v) return <span className="text-gray-400">-</span>;
        return <span className="whitespace-nowrap">{v}</span>;
      },
    },
    {
      accessorKey: 'oos_equipment',
      header: 'OOS Equipment',
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.oos_equipment;
        if (v !== 'Y' && v !== 'N') return <span className="text-gray-400">-</span>;
        return (
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
              v === 'Y'
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {v}
          </span>
        );
      },
    },
    {
      id: 'repair_order_recnos',
      header: 'Equip Repair Orders',
      // accessorFn drives sorting/filtering and the Excel export (clean
      // comma-joined recnos); the cell renderer handles the clickable links.
      accessorFn: (row) => (row.repair_order_recnos || []).join(', '),
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const recnos = row.original.repair_order_recnos || [];
        if (recnos.length === 0) return <span className="text-gray-400">-</span>;
        if (!canViewRepairOrders) {
          return <span className="whitespace-normal break-words">{recnos.join(', ')}</span>;
        }
        return (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            {recnos.map((recno) => (
              <button
                key={recno}
                type="button"
                onClick={(e) => { e.stopPropagation(); setRepairOrderRecno(recno); }}
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300"
              >
                {recno}
              </button>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'loadempty',
      header: 'Status',
      size: 90,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.loadempty;
        return (
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
              v === 'L'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {v === 'L' ? 'Loaded' : v === 'E' ? 'Empty' : 'N/A'}
          </span>
        );
      },
    },
    {
      accessorKey: 'pluggedin',
      header: 'Plugged',
      size: 90,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) =>
        row.original.pluggedin === 'Y' ? (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 whitespace-nowrap">
            Plugged In
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      id: 'reefer_genset',
      header: 'Genset',
      size: 190,
      accessorFn: (row) => `${formatGenset(row.required_genset, 'Not specified')} / ${formatGenset(row.last_reefer_genset, 'Not recorded')}`,
      cell: ({ row }) => (
        <div className="flex flex-col text-xs leading-tight">
          <span>Required: {formatGenset(row.original.required_genset, 'Not specified')}</span>
          <span>Submitted: {formatGenset(row.original.last_reefer_genset, 'Not recorded')}</span>
        </div>
      ),
    },
    {
      id: 'last_reefer_log',
      header: 'Last Reefer Log',
      accessorFn: (row) => row.last_reefer_logged ?? '',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => {
        const temp = row.original.last_reefer_temp;
        const scale = row.original.last_reefer_tempscale;
        const logged = row.original.last_reefer_logged;
        if (!logged) return <span className="text-gray-400">-</span>;
        return (
          <div className="flex flex-col leading-tight">
            <span className="font-semibold text-green-700 dark:text-green-400">
              {temp ? `${temp}°${scale || ''}`.trim() : '-'}
            </span>
            <span className="text-xs text-muted">
              {formatDateTime(logged)}
            </span>
          </div>
        );
      },
    },
    {
      id: 'customer',
      header: 'Customer',
      accessorFn: (row) => row.customer_name || row.companyname || 'Unknown',
      size: 220,
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'cmspronumber',
      header: 'PRO',
      size: 100,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const pro = row.original.cmspronumber;
        if (!pro || pro <= 0) {
          // ATTACH PRO badge — opens the modal so the user can search for a
          // pronumber and bind it to this yardinv row. Mirrors the SSL Bare
          // Chassis flow on the Dispatch Dashboard.
          const yardinv_recno = row.original.recno;
          const chassisno = row.original.chassisno || '';
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setAttachPronumberModal({ yardinv_recno, chassisno });
              }}
              className="text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-300 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 24" className="h-5">
                <rect x="1" y="3" width="88" height="18" rx="3" fill="transparent" stroke="currentColor" strokeWidth="2" />
                <text x="45" y="16" fontSize="10" fontWeight="600" textAnchor="middle" fill="currentColor">ATTACH PRO</text>
              </svg>
            </button>
          );
        }
        // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
        // trackViewedColumnId="cmspronumber" wrapper.
        return (
          <button
            type="button"
            onClick={() => openOrderDrawer(pro)}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            {pro}
          </button>
        );
      },
    },
    {
      accessorKey: 'booking_number',
      // BOL/Booking from the order (ordersall.bol) — booking # on exports, BOL
      // on imports. Hidden by default; dispatchers enable it via the column
      // picker so they can read export booking #s without opening each PRO.
      header: 'BOL / Booking #',
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.booking_number;
        if (!v) return <span className="text-gray-400">-</span>;
        return <span className="whitespace-nowrap">{v}</span>;
      },
    },
    {
      accessorKey: 'movetype',
      // Order movetype. Hidden by default — enabled from the column picker.
      header: 'Move Type',
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.movetype;
        if (!v) return <span className="text-gray-400">-</span>;
        return <span className="whitespace-nowrap">{v}</span>;
      },
    },
    {
      accessorKey: 'dispatcher_name',
      // Dispatcher on the attached order. Hidden by default — enabled from the
      // column picker.
      header: 'Order Dispatcher',
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.dispatcher_name;
        if (!v) return <span className="text-gray-400">-</span>;
        return <span className="whitespace-nowrap">{v}</span>;
      },
    },
    {
      accessorKey: 'perdiem_start_date',
      header: 'Per Diem Date',
      size: 120,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.perdiem_start_date;
        return v ? formatShortDate(v) : <span className="text-gray-400">-</span>;
      },
    },
    {
      accessorKey: 'perdiem_amount',
      header: 'Per Diem Amount',
      size: 130,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const v = row.original.perdiem_amount;
        if (v == null || v <= 0) return <span className="text-gray-400">-</span>;
        return (
          <span className="font-semibold text-red-700 dark:text-red-400">
            ${v.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    },
    {
      accessorKey: 'drop_release_datetime',
      header: 'Drop Release',
      size: 140,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.drop_release_datetime;
        return v ? formatDateTime(v) : <span className="text-gray-400">-</span>;
      },
    },
    {
      accessorKey: 'drop_release_memo',
      header: 'Drop Release Memo',
      size: 220,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const v = row.original.drop_release_memo;
        if (!v) return <span className="text-gray-400">-</span>;
        return <span title={v} className="line-clamp-2">{v}</span>;
      },
    },
    {
      accessorKey: 'first_rcv_date',
      header: 'First RCV',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        if (row.original.is_rolling) {
          return <span className="font-medium text-amber-600 dark:text-amber-400">Rolling</span>;
        }
        const v = row.original.first_rcv_date;
        return v ? formatShortDate(v) : <span className="text-gray-400">-</span>;
      },
    },
    {
      accessorKey: 'delivery_date',
      header: 'Delivery Date',
      size: 120,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.delivery_date;
        return v ? formatShortDate(v) : <span className="text-gray-400">-</span>;
      },
    },
    {
      accessorKey: 'delivery_appt_date',
      header: 'Delivery Appt',
      size: 120,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.delivery_appt_date;
        return v ? formatShortDate(v) : <span className="text-gray-400">-</span>;
      },
    },
    {
      accessorKey: 'cutoff_datetime',
      header: 'Cutoff',
      size: 140,
      enableSorting: true,
      cell: ({ row }) => {
        if (row.original.is_rolling) {
          return <span className="font-medium text-amber-600 dark:text-amber-400">Rolling</span>;
        }
        const v = row.original.cutoff_datetime;
        return v ? formatDateTime(v) : <span className="text-gray-400">-</span>;
      },
    },
    {
      accessorKey: 'dispatched_to_terminate',
      header: 'Disp. to Terminate',
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        if (!row.original.dispatched_to_terminate) {
          return <span className="text-gray-400">-</span>;
        }
        // A driver is assigned to terminate. If that terminate is scheduled for
        // today or later, flag it as upcoming rather than a plain "Yes".
        const appt = row.original.terminate_appt_date;
        if (appt) {
          const parsed = new Date(appt + 'T00:00:00');
          if (!isNaN(parsed.getTime())) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            parsed.setHours(0, 0, 0, 0);
            if (parsed.getTime() >= today.getTime()) {
              return (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 whitespace-nowrap">
                  {formatShortDate(appt)}
                </span>
              );
            }
          }
        }
        return (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 whitespace-nowrap">
            {appt ? `Yes ${formatShortDate(appt)}` : 'Yes'}
          </span>
        );
      },
    },
    {
      accessorKey: 'indatetime',
      header: 'Added',
      size: 130,
      enableSorting: true,
      cell: ({ row }) => formatDateTime(row.original.indatetime),
    },
    {
      accessorKey: 'last_checked_datetime',
      header: 'Last Checked',
      size: 140,
      enableSorting: true,
      cell: ({ row }) => {
        if (!row.original.last_checked_datetime) {
          return <span className="text-gray-400 italic">never</span>;
        }
        return (
          <span className="text-green-700 dark:text-green-400 font-medium">
            {formatDateTime(row.original.last_checked_datetime)}
          </span>
        );
      },
    },
    {
      accessorKey: 'last_checked_by',
      header: 'Checked By',
      size: 130,
      enableSorting: true,
      cell: ({ row }) => {
        if (!row.original.last_checked_by) {
          return <span className="text-gray-400 italic">—</span>;
        }
        return <span>{row.original.last_checked_by}</span>;
      },
    },
  ], [openOrderDrawer, canViewRepairOrders]);

  // Not In CMS rows have no order attached, so the order-derived columns are
  // always blank — drop them from the column set (and the show/hide picker
  // and Excel export) when that bucket is active.
  const displayedColumns = useMemo<ColumnDef<YardInventoryItem>[]>(() => {
    if (activeBucket !== 'not-in-cms') return columns;
    return columns.filter((c) => {
      const key = (c as { id?: string; accessorKey?: string }).id
        ?? (c as { id?: string; accessorKey?: string }).accessorKey;
      return !key || !NOT_IN_CMS_HIDDEN_COLS.has(key);
    });
  }, [columns, activeBucket]);

  // Format a leg-arrival timestamp showing year only when it isn't the current
  // year (mirrors the Dispatch Dashboard yard_arrival_datetime cell).
  const formatYardArrival = useCallback((value: string | null) => {
    if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') return '-';
    let dateObj: Date;
    if (value.includes('T')) {
      dateObj = new Date(value);
    } else if (value.includes(' ')) {
      const [datePart, timePart] = value.split(' ');
      dateObj = new Date(`${datePart}T${timePart}`);
    } else {
      dateObj = new Date(`${value}T00:00:00`);
    }
    if (isNaN(dateObj.getTime())) return value;
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    const yearSuffix = year === new Date().getFullYear() ? '' : `/${String(year).slice(-2)}`;
    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${month}/${day}${yearSuffix} ${hours}:${minutes} ${ampm}`;
  }, []);

  // Columns for the Missing Containers bucket. Sourced from orderscurrent —
  // no yardinv row to anchor on, so the column set diverges from the other
  // four buckets.
  const missingContainersColumns = useMemo<ColumnDef<MissingContainerItem>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'PRO',
      size: 100,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const pro = row.original.pronumber;
        if (!pro || pro <= 0) return <span className="text-gray-400">-</span>;
        // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
        // trackViewedColumnId="pronumber" wrapper.
        return (
          <button
            type="button"
            onClick={() => openOrderDrawer(pro)}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            {pro}
          </button>
        );
      },
    },
    {
      accessorKey: 'movetype',
      header: 'Move Type',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.movetype || <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: 'container',
      header: 'Container',
      size: 130,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.container || <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: 'order_location',
      header: 'Order Location',
      size: 200,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.order_location || <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: 'yard_arrival_datetime',
      header: 'Yard Arrival',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => formatYardArrival(row.original.yard_arrival_datetime),
    },
    {
      accessorKey: 'equiptype',
      header: 'Equip Type',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.equiptype || <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: 'chassisid',
      header: 'Chassis #',
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.chassisid || <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: 'chassistype',
      header: 'Chassis Type',
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.chassistype || <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: 'customer_name',
      header: 'Customer',
      size: 220,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.customer_name || <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: 'dispatcher_name',
      header: 'Dispatcher',
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => row.original.dispatcher_name || <span className="text-gray-400">-</span>,
    },
  ], [openOrderDrawer, formatYardArrival]);

  const inventoryGrid = useAdditionalFieldColumns<YardInventoryItem>(inventoryFields, {
    baseColumns: displayedColumns, binding: 'all',
    coveredFields: { cmspronumber: ['pronumber'] },
  });
  const missingGrid = useAdditionalFieldColumns<MissingContainerItem>(missingFields, {
    baseColumns: missingContainersColumns, binding: 'all',
  });

  // Apply the employee yard filter client-side. Guards see only their yard
  // already (data was fetched scoped). When no yards are selected, show all.
  const filteredInventory = useMemo<YardInventoryItem[]>(() => {
    if (selectedYard !== null) return inventory;
    if (selectedYardIds.length === 0) return inventory;
    const allowed = new Set(selectedYardIds);
    return inventory.filter((row) => allowed.has(row.yardrecno));
  }, [inventory, selectedYard, selectedYardIds]);

  // Missing Containers rows expose `yardrecnos` (the yards their destination
  // maps to). A row passes the multi-yard filter if any of its mapped yards
  // is in the selection.
  const filteredMissingContainers = useMemo<MissingContainerItem[]>(() => {
    if (selectedYard !== null) {
      return missingContainers.filter((row) => row.yardrecnos.includes(selectedYard!));
    }
    if (selectedYardIds.length === 0) return missingContainers;
    const allowed = new Set(selectedYardIds);
    return missingContainers.filter((row) =>
      row.yardrecnos.some((y) => allowed.has(y)),
    );
  }, [missingContainers, selectedYard, selectedYardIds]);

  const totalPerDiem = useMemo(
    () => filteredInventory.reduce((sum, row) => sum + (row.perdiem_amount || 0), 0),
    [filteredInventory],
  );

  // Yard column is only useful when more than one yard is in view (i.e. no
  // single-yard scope from guard or multi-select).
  const showYardColumn = selectedYard === null && selectedYardIds.length !== 1;
  const defaultColumnVisibility = useMemo<Record<string, boolean>>(() => {
    // Booking #, Move Type and Order Dispatcher are always available-but-hidden
    // (dispatchers opt in via the column picker); the Yard column only hides
    // when a single yard is in view.
    const vis: Record<string, boolean> = {
      booking_number: false,
      movetype: false,
      dispatcher_name: false,
    };
    if (!showYardColumn) vis.yard_name = false;
    return vis;
  }, [showYardColumn]);

  // Yard selector options carry the last yard event datetime alongside the
  // name (e.g. "Newark — 4/27/2026 3:45 PM") so dispatchers can see at a glance
  // how recently each yard had activity. Falls back to the bare name when a
  // yard has no events. Only the dropdown label is enriched; locname stays
  // clean for the selected-chip label and card title below.
  const yardOptions = useMemo<MultiSelectOption[]>(
    () => yards.map((y) => {
      const lastEvent = formatFullDateTime(y.last_event);
      return {
        value: String(y.recno),
        label: lastEvent ? `${y.locname} — ${lastEvent}` : y.locname,
      };
    }),
    [yards],
  );

  // Bucket label used in the table header. The leftmost stat ("Yard
  // Inventory") keeps the legacy "On Yard" wording so the page reads like
  // before when nothing else is selected.
  const activeBucketTitle = useMemo(() => {
    switch (activeBucket) {
      case 'yard-inventory': return 'On Yard';
      case 'terminated-on-yard': return 'Terminated On Yard';
      case 'ssl-bare-chassis': return 'SSL Bare Chassis On Yard';
      case 'leg-yard-mismatch': return 'Leg Yard Mismatch';
      case 'missing-containers': return 'Missing Containers';
      case 'not-in-cms': return 'Not In CMS';
    }
  }, [activeBucket]);

  const yardScopeLabel = useMemo(() => {
    if (selectedYard !== null) {
      return yards.find((y) => y.recno === selectedYard)?.locname ?? null;
    }
    if (selectedYardIds.length === 0) return 'All Yards';
    if (selectedYardIds.length === 1) {
      const id = selectedYardIds[0];
      return yards.find((y) => y.recno === id)?.locname ?? null;
    }
    return `${selectedYardIds.length} yards`;
  }, [selectedYard, selectedYardIds, yards]);

  const cardTitle = useMemo(
    () => (yardScopeLabel ? `${activeBucketTitle} — ${yardScopeLabel}` : activeBucketTitle),
    [activeBucketTitle, yardScopeLabel],
  );

  const isMissingContainersActive = activeBucket === 'missing-containers';

  return (
    <ViewedProsProvider>
    <div className="h-full w-full flex flex-col px-4 pt-4 pb-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h1 className="text-2xl font-bold dark:text-white">On Yard Report</h1>
      </div>

      {/* Yard Selector (employee view) + Total Per Diem (yardinv buckets only).
          Rendered above the bucket counts so it's clear the yard selection
          scopes the entire stat row. */}
      {!selectedYard && (
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px] max-w-md">
            <label className="block text-sm font-medium mb-1 dark:text-white">Select Yard(s)</label>
            <MultiSelect
              options={yardOptions}
              value={selectedYardIds.map(String)}
              onChange={(values) => setSelectedYardIds(values.map(Number))}
              placeholder="All Yards"
              enableSearch
              showClearOption
              clearOptionLabel="All Yards"
              preserveOrder
            />
          </div>
          {!isMissingContainersActive && (
            <div className="px-3 py-2 rounded bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-sm text-gray-700 dark:text-gray-200">
              Total Per Diem:{' '}
              <span className="font-semibold text-red-700 dark:text-red-400">
                ${totalPerDiem.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      )}

      {selectedYard !== null && !isMissingContainersActive && (
        <div className="mb-3">
          <span className="px-3 py-2 rounded bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-sm text-gray-700 dark:text-gray-200">
            Total Per Diem:{' '}
            <span className="font-semibold text-red-700 dark:text-red-400">
              ${totalPerDiem.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </span>
        </div>
      )}

      {/* Yard Reports buckets — counts respect the yard selection above.
          Clicking a stat swaps the table below to show that bucket's rows.
          "Yard Inventory" is the default selection. */}
      <div className="mb-3">
        <StatsGrid
          stats={yardBucketStats}
          loading={bucketCountsLoading}
          columns={7}
          rows={1}
          size="sm"
        />
      </div>

      <div className="flex-1 min-h-0">
        {isMissingContainersActive ? (
          <TanTable
            // Distinct key per view: the missing-containers and inventory grids share
            // this ternary slot with different settingsKeys, so without a key React
            // reuses ONE TanTable across the bucket switch and its autosave cross-writes
            // the other view's saved layout. Key = fresh mount; seed avoids the flash.
            key="yard-reports-missing-containers"
            data={filteredMissingContainers}
            columns={missingGrid.columns}
            availableFields={missingGrid.availableFields}
            additionalFields={missingFields.additionalFields}
            onAdditionalFieldsChange={missingFields.onAdditionalFieldsChange}
            trackViewedColumnId="pronumber"
            getViewedKey={(r) => (r.pronumber && r.pronumber > 0 ? r.pronumber : null)}
            loading={loading}
            settingsKey="yard-reports-missing-containers"
            fieldName="yard-reports-missing-containers"
            seedLayoutFromCache
            cardTitle={cardTitle}
            searchPlaceholder="Search PRO, container, location..."
            enableSearch={true}
            enableColumnFilters={true}
            enableExcelExport={true}
            enableShowHideCols={true}
            enableZebraStriping={false}
            fillContainer={true}
          />
        ) : (
          <TanTable
            // See the missing-containers grid above — the key tracks the per-bucket
            // settingsKey so each bucket is its own TanTable instance and never
            // cross-writes another bucket's saved layout.
            key={inventorySettingsKey}
            data={filteredInventory}
            columns={activeBucket === 'not-in-cms' ? displayedColumns : inventoryGrid.columns}
            availableFields={activeBucket === 'not-in-cms' ? undefined : inventoryGrid.availableFields}
            additionalFields={activeBucket === 'not-in-cms' ? undefined : inventoryFields.additionalFields}
            onAdditionalFieldsChange={inventoryFields.onAdditionalFieldsChange}
            // ATTACH PRO rows (no/zero pronumber) return null: untracked, so
            // clicking the badge never marks anything viewed.
            trackViewedColumnId="cmspronumber"
            getViewedKey={(r) => (r.cmspronumber && r.cmspronumber > 0 ? r.cmspronumber : null)}
            loading={loading}
            settingsKey={inventorySettingsKey}
            fieldName={inventorySettingsKey}
            seedLayoutFromCache
            cardTitle={cardTitle}
            searchPlaceholder="Search equipment, chassis, customer, PRO..."
            enableSearch={true}
            enableColumnFilters={true}
            enableExcelExport={true}
            enableShowHideCols={true}
            enableZebraStriping={false}
            defaultColumnVisibility={defaultColumnVisibility}
            getRowClassName={(row) => getPerDiemUrgency(row.perdiem_start_date)}
            fillContainer={true}
          />
        )}
      </div>

      {/* Attach Pro modal — opened from the ATTACH PRO badge on a yardinv
          row with no pronumber. The modal's default details base path is
          /yard/pronumber, so no override needed here. */}
      <AttachPronumberModal
        isOpen={attachPronumberModal !== null}
        onClose={() => setAttachPronumberModal(null)}
        onConfirm={handleAttachPronumber}
        chassisno={attachPronumberModal?.chassisno || ''}
      />

      {/* Order Details Drawer (opened from PRO link) */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />

      {/* Repair Order Drawer (opened from an Equip Repair Orders link). Reuses
          the Fleet Management drawer, which fetches its own data by recno. */}
      <RepairOrderDrawer
        isOpen={repairOrderRecno !== null}
        onClose={() => setRepairOrderRecno(null)}
        orderRecno={repairOrderRecno}
        onSaved={() => { loadBucketData({ silent: true }); }}
      />
    </div>
    </ViewedProsProvider>
  );
}
